import styled from "styled-components";

import { colors, fonts, fontSizes, fontWeights, radius, transitions } from "@/styles/design-system";

/* ─── Layout ─── */

export const PageWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    padding: 40px;
  }
`;

export const PageTitle = styled.h1`
  font-family: ${fonts.heading};
  font-size: ${fontSizes["3xl"]};
  font-weight: ${fontWeights.bold};
  color: ${colors.text.heading};
  margin: 0;
`;

/* ─── Tab Bar ─── */

/* Segmented pill tabs beside the page title (via AccountLayout headerExtra),
   mirroring the /music PillTabsContainer + PillTab 1:1 (bordered container, 36px
   pills, active peach fill, blue/tan icons). */
export const TabBar = styled.div`
  display: flex;
  position: relative;
  /* center, not flex-start: it sits beside the page heading and has to line up
     with it rather than hang from the top of the row. */
  align-self: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
`;

export const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  border-radius: 6px;
  padding: 0 20px;
  height: 36px;
  color: #fbf4ec;
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};
  white-space: nowrap;

  &:hover {
    color: #fbf4ec;
    background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "rgba(255, 255, 255, 0.06)")};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    stroke: ${({ $active }) => ($active ? "#9cd3fd" : "#e0d2c1")};
    transition: stroke 0.2s ease;
    ${({ $active }) => ($active ? "filter: drop-shadow(0 0 4px rgba(156, 211, 253, 0.3));" : "")}
  }
`;

/* ─── Content Panel ─── */

export const Card = styled.div`
  background: ${colors.surface.panel};
  border: 1px solid ${colors.border.veryLight};
  border-radius: ${radius.xl};
`;

export const CardInner = styled.div``;

/* ─── Table Header ─── */

export const TableHeader = styled.div`
  display: none;
  padding: 14px 20px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: rgba(251, 244, 236, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${colors.border.veryLight};
  align-items: center;

  span {
    text-align: left;
  }

  @media (min-width: 768px) {
    display: grid;
  }
`;

export const DownloadsTableHeader = styled(TableHeader)`
  grid-template-columns: 40px 48px 1fr 120px 100px;
  gap: 16px;
`;

export const LicensesTableHeader = styled(TableHeader)`
  grid-template-columns: 48px minmax(60px, 1.2fr) 1fr 1fr 1fr 1fr;
  gap: 16px;
`;

/* ─── Table Row ─── */

export const TableRow = styled.div`
  padding: 12px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  border-bottom: 1px solid rgba(186, 144, 92, 0.06);
  transition: all ${transitions.normal};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }
`;

export const DownloadsRow = styled(TableRow)`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 40px 48px 1fr 120px 100px;
    gap: 16px;
  }
`;

export const LicensesRow = styled(TableRow)`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 48px minmax(60px, 1.2fr) 1fr 1fr 1fr 1fr;
    gap: 16px;
  }
`;

export const RowNumber = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.body};
  color: rgba(251, 244, 236, 0.3);
  min-width: 40px;
`;

export const Thumbnail = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${radius.sm};
  background: rgba(186, 144, 92, 0.08);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ThumbnailPlayBtn = styled.button<{ $playing?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $playing }) => ($playing ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.35)")};
  border: none;
  cursor: pointer;
  opacity: 1;
  transition: background ${transitions.fast};

  svg {
    width: 18px;
    height: 18px;
    fill: #fff;
    stroke: none;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  }
`;

export const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const TrackTitle = styled.span`
  font-family: ${fonts.heading};
  font-size: ${fontSizes.lg};
  font-weight: ${fontWeights.medium};
  color: ${colors.text.primary};
  line-height: 1.1;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TrackArtist = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.regular};
  color: ${colors.text.artist};
`;

/* ─── Buttons ─── */

export const ActionButton = styled.button`
  background: ${colors.surface.glass};
  border: none;
  border-radius: ${radius.md};
  padding: 8px 16px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: #ac8d75;
  cursor: pointer;
  transition: all ${transitions.normal};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${colors.surface.glassHover};
    color: ${colors.gold.accent};
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.5;
  }
`;

export const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: ${colors.surface.glass};
  border: 1px solid ${colors.border.medium};
  border-radius: 20px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.regular};
  color: ${colors.text.primary};
  cursor: pointer;
  transition: all ${transitions.normal};
  white-space: nowrap;

  &:hover {
    border-color: rgba(186, 144, 92, 0.4);
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ConnectButton = styled.button`
  background: ${colors.accent.blue};
  border: 1px solid ${colors.accent.blue};
  border-radius: 20px;
  padding: 7px 14px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.bold};
  color: ${colors.accent.blueDeep};
  cursor: pointer;
  transition: all ${transitions.normal};
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    box-shadow: 0 0 14px rgba(156, 211, 253, 0.4);
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

export const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: 1px solid transparent;
  border-radius: ${radius.md};
  padding: 14px 32px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  color: ${colors.brown.darkest};
  cursor: pointer;
  transition: all ${transitions.normal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid ${colors.border.default};
  border-radius: ${radius.md};
  padding: 14px 32px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  color: ${colors.text.link};
  cursor: pointer;
  transition: all ${transitions.normal};
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(186, 144, 92, 0.08);
    border-color: rgba(186, 144, 92, 0.5);
  }
`;

/* ─── Status Badges ─── */

const statusColors: Record<string, { bg: string; text: string; border?: string }> = {
  /* Green — resolved / good */
  licensed: { bg: "rgba(123, 219, 123, 0.1)", text: "#7bdb7b", border: "rgba(123, 219, 123, 0.4)" },
  completed: { bg: "rgba(123, 219, 123, 0.1)", text: "#7bdb7b", border: "rgba(123, 219, 123, 0.4)" },
  ready: { bg: "rgba(123, 219, 123, 0.1)", text: "#7bdb7b", border: "rgba(123, 219, 123, 0.4)" },
  /* Gold — in progress */
  checking: { bg: "rgba(251, 244, 236, 0.06)", text: "rgba(251, 244, 236, 0.6)", border: "rgba(251, 244, 236, 0.15)" },
  processing: { bg: "rgba(245, 166, 35, 0.1)", text: "#f5a623", border: "rgba(245, 166, 35, 0.3)" },
  connected: { bg: "rgba(186, 144, 92, 0.15)", text: "#FFDCA8", border: "rgba(186, 144, 92, 0.3)" },
  /* Neutral — waiting for action */
  "not connected": { bg: "transparent", text: "#ad8b79" },
  active: { bg: "rgba(186, 144, 92, 0.1)", text: "#FFDCA8", border: "rgba(186, 144, 92, 0.3)" },
  /* Yellow — action needed */
  "credit text": { bg: "rgba(255, 215, 0, 0.1)", text: "#ffd700", border: "rgba(255, 215, 0, 0.4)" },
  "credit text required": { bg: "rgba(255, 215, 0, 0.1)", text: "#ffd700", border: "rgba(255, 215, 0, 0.4)" },
  /* Red — blocked */
  "music use": { bg: "rgba(255, 68, 68, 0.1)", text: "#ff4444", border: "rgba(255, 68, 68, 0.4)" },
  revoked: { bg: "rgba(255, 68, 68, 0.1)", text: "#ff4444", border: "rgba(255, 68, 68, 0.4)" },
  refunded: { bg: "rgba(244, 67, 54, 0.15)", text: "#ef9a9a", border: "rgba(244, 67, 54, 0.3)" },
  expired: { bg: "rgba(244, 67, 54, 0.1)", text: "#ef9a9a", border: "rgba(244, 67, 54, 0.3)" },
};

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: ${fontWeights.semibold};
  white-space: nowrap;
  background: ${({ $status }) => statusColors[$status.toLowerCase()]?.bg || "rgba(186, 144, 92, 0.15)"};
  color: ${({ $status }) => statusColors[$status.toLowerCase()]?.text || "#FFDCA8"};
  border: 1px solid ${({ $status }) => statusColors[$status.toLowerCase()]?.border || "transparent"};
`;

/* ─── Warning Notice ─── */

export const WarningNotice = styled.div`
  background: ${colors.surface.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${colors.border.white};
  border-radius: ${radius.xl};
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: rgba(251, 244, 236, 0.6);
  line-height: 1.6;

  strong {
    color: ${colors.gold.accent};
  }
`;

export const WarningIcon = styled.span`
  font-size: ${fontSizes.lg};
  flex-shrink: 0;
  line-height: 1.4;
  color: ${colors.gold.accent};
`;

export const PageDescription = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.muted};
  margin: -8px 0 20px 0;
  padding-left: 6px;
  line-height: 1.6;
`;

/* ─── YouTube Channel Connection ─── */

export const ChannelCard = styled.div`
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.xl};
  padding: 20px;
  margin-bottom: 20px;
`;

export const ChannelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const ChannelIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 68, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ff4444;
`;

export const ChannelTitle = styled.div`
  font-family: ${fonts.heading};
  font-size: 1.125rem;
  font-weight: ${fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: 2px;
`;

export const ChannelDesc = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  line-height: 1.5;
`;

export const ChannelConnected = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ChannelUrl = styled.a`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.accent.blue};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  &:hover {
    color: ${colors.text.primary};
  }
`;

export const ChannelActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

export const ChannelActionBtn = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: ${({ $danger }) => ($danger ? colors.accent.red : colors.accent.blue)};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${radius.xs};
  transition: background ${transitions.fast};

  &:hover {
    background: ${({ $danger }) => ($danger ? "rgba(248, 113, 113, 0.1)" : "rgba(156, 211, 253, 0.1)")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ChannelForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ChannelInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(75, 55, 39, 0.8);
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.md};
  color: ${colors.text.primary};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  outline: none;
  transition: border-color ${transitions.normal};
  box-sizing: border-box;

  &::placeholder {
    color: rgba(251, 244, 236, 0.25);
  }

  &:focus {
    border-color: rgba(156, 211, 253, 0.4);
  }
`;

export const ChannelFormActions = styled.div`
  display: flex;
  gap: 10px;
`;

export const ChannelSaveBtn = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  color: ${colors.brown.darkest};
  border: none;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 20px rgba(186, 144, 92, 0.3);
  }

  &:disabled {
    background: ${colors.surface.card};
    color: rgba(251, 244, 236, 0.3);
    border: 1px solid ${colors.border.veryLight};
    transform: none;
    box-shadow: none;
    cursor: default;
  }
`;

export const ChannelUpgrade = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.border.veryLight};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.gold.light};
  text-decoration: none;
  transition: color ${transitions.fast};

  &:hover {
    color: ${colors.text.primary};
  }
`;

export const ChannelCancelBtn = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid ${colors.border.default};
  border-radius: ${radius.md};
  color: ${colors.text.link};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.medium};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(235, 194, 142, 0.08);
  }
`;

export const NoticePill = styled.span`
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(255, 220, 168, 0.12);
  border: 1px solid rgba(255, 220, 168, 0.25);
  color: ${colors.gold.accent};
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: ${fontWeights.medium};
  white-space: nowrap;
`;

export const StatusAlert = styled.div<{ $color: "yellow" | "red" }>`
  background: ${({ $color }) => $color === "red" ? "rgba(255, 68, 68, 0.06)" : "rgba(255, 215, 0, 0.06)"};
  border: 1px solid ${({ $color }) => $color === "red" ? "rgba(255, 68, 68, 0.2)" : "rgba(255, 215, 0, 0.2)"};
  border-radius: ${radius.xl};
  padding: 14px 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${({ $color }) => $color === "red" ? "rgba(255, 130, 130, 0.9)" : "rgba(255, 220, 150, 0.9)"};
  line-height: 1.6;
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: ${({ $color }) => $color === "red" ? "rgba(255, 68, 68, 0.1)" : "rgba(255, 215, 0, 0.1)"};
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

/* ─── Order Cards ─── */

export const OrderCard = styled.div`
  background: ${colors.surface.panel};
  border: 1px solid ${colors.border.veryLight};
  border-radius: ${radius.lg};
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(75, 55, 39, 0.5);
    border-color: rgba(186, 144, 92, 0.12);
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const OrderNumber = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  color: ${colors.text.primary};
`;

export const OrderMeta = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.mutedLight};
`;

export const OrderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const OrderTotal = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.lg};
  font-weight: ${fontWeights.bold};
  color: ${colors.gold.accent};
`;

/* ─── Files Dropdown ─── */

export const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const DropdownMenu = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? "block" : "none")};
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
  min-width: 200px;
  background: ${colors.menu.bg};
  border-radius: ${radius.lg};
  padding: 6px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
`;

export const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.menu.text};
  cursor: pointer;
  border-radius: ${radius.sm};
  text-align: left;
  transition: background ${transitions.fast};

  &:hover {
    background: ${colors.menu.hoverBg};
  }
`;

/* ─── Video Thumbnail ─── */

export const VideoThumb = styled.div`
  width: 80px;
  height: 45px;
  border-radius: ${radius.sm};
  background: rgba(186, 144, 92, 0.06);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

`;

/* ─── Modal ─── */

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

export const ModalContainer = styled.div`
  position: relative;
  background: #59412e;
  border: 1px solid rgba(235, 194, 142, 0.3);
  border-radius: 24px;
  width: 100%;
  max-width: 520px;
  /* dvh so the modal tracks the VISIBLE viewport as the iOS Safari bar
     shows/hides (vh used the large viewport and clipped the modal). */
  max-height: 90dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
`;

export const ModalHeader = styled.div`
  padding: 40px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ModalTitle = styled.h2`
  font-family: ${fonts.body};
  font-size: ${fontSizes["2xl"]};
  font-weight: ${fontWeights.semibold};
  color: ${colors.text.heading};
  margin: 0;
`;

export const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid rgba(235, 194, 142, 0.3);
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(235, 194, 142, 0.35);
    border-color: rgba(235, 194, 142, 0.5);
  }
`;

export const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ModalInput = styled.input`
  width: 100%;
  background: ${colors.surface.glass};
  border: 1px solid ${colors.border.input};
  border-radius: ${radius.md};
  padding: 12px 16px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.primary};
  outline: none;
  transition: all ${transitions.normal};
  box-sizing: border-box;

  &::placeholder {
    color: rgba(251, 244, 236, 0.3);
  }

  &:focus {
    border-color: rgba(186, 144, 92, 0.4);
    background: ${colors.surface.glassHover};
  }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 0 24px 24px;
`;

export const ModalVideoPreview = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${colors.border.veryLight};
  border-radius: ${radius.lg};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ModalVideoThumb = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${radius.md};
  background: rgba(186, 144, 92, 0.06);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ModalVideoTitle = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  color: ${colors.text.primary};
`;

export const ModalConfirmText = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.secondary};
  margin: 0 0 8px;
  line-height: 1.6;
`;

/* ─── Empty State ─── */

export const EmptyState = styled.div`
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

export const EmptyIcon = styled.span`
  font-size: 2.5rem;
  opacity: 0.3;
`;

export const EmptyText = styled.p`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: rgba(251, 244, 236, 0.35);
  margin: 0;
`;

/* ─── Mobile Labels ─── */

export const MobileLabel = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: ${fontWeights.medium};
  color: rgba(251, 244, 236, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;

  @media (min-width: 768px) {
    display: none;
  }
`;

/* ─── Status Info ─── */

export const StatusWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-left: -2px;
`;

export const MoreInfoLink = styled.a`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.link};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${colors.gold.light};
  }
`;

export const StatusPopupCreditBox = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(173, 139, 121, 0.3);
  border-radius: ${radius.md};
  padding: 14px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.secondary};
  white-space: pre-wrap;
  line-height: 1.6;
`;

export const CopyButton = styled.button<{ $copied?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ $copied }) => ($copied ? "linear-gradient(135deg, #5a9a5a 0%, #7bdb7b 100%)" : colors.surface.glass)};
  border: 1px solid ${({ $copied }) => ($copied ? "rgba(123, 219, 123, 0.4)" : colors.border.medium)};
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: ${fontWeights.medium};
  color: ${({ $copied }) => ($copied ? "#fff" : colors.text.secondary)};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    border-color: ${({ $copied }) => ($copied ? "rgba(123, 219, 123, 0.6)" : "rgba(186, 144, 92, 0.4)")};
    color: ${({ $copied }) => ($copied ? "#fff" : colors.text.primary)};
  }
`;

